import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextHeadline, TextLabel1, TextTitle1, TextTitle3 } from '@cbhq/cds-web/typography';
import { memo, Suspense, useCallback, useMemo } from 'react';
import { useAccount } from 'wagmi';
import { useRouter } from 'next/navigation';
import { PressableOpacity } from '@cbhq/cds-web/system';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { usePositions } from '@/onchain/hooks/usePositions';
import { DisplayType, format } from '@/utils/numberFormatter';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { ERROR_MESSAGE_PORTFOLIO_SUMMARY_FAILED } from '@/app/utils/errors';

const SUMMARY_ITEMS = [
  { label: 'Available fees', value: 0 },
  { label: 'Number of positions', value: 0 },
  //Remove liquidity balanc
  //{ label: 'LIQUIDITY BALANCE', value: 0 },
];
const SUMMARY_ITEMS_GAP = 3;
const SUMMARY_ITEMS_FLEX_BASIS = `calc((100% - ${
  SUMMARY_ITEMS.length - 1
} * var(--spacing-${SUMMARY_ITEMS_GAP})) / ${SUMMARY_ITEMS.length})`;

function PortfolioSummaryItemsLoading() {
  return (
    <>
      {SUMMARY_ITEMS.map((item) => {
        return (
          <VStack key={item.label} gap={1.5} flexBasis={SUMMARY_ITEMS_FLEX_BASIS} flexGrow={1}>
            <TextHeadline as="span" noWrap color="fgMuted">
              {item.label}
            </TextHeadline>
            <Fallback width="80%" height={36} shape="squircle" />
          </VStack>
        );
      })}
    </>
  );
}

function PortfolioSummaryItems() {
  // Use the new combined hook that returns both IDs and details
  const { positions: positionDetails, pagination } = usePositions();
  const { isPhone, isPhoneLandscape, isTablet } = useBreakpoints();
  const isMobile = isPhone || isPhoneLandscape || isTablet;

  // Use total count from pagination, not just current page length
  const positionCount = pagination.totalItems;

  const unclaimedFees = useMemo(
    () =>
      (positionDetails ?? []).reduce(
        (acc, { totalUnclaimedFeesNotional }) => acc + totalUnclaimedFeesNotional,
        0,
      ),
    [positionDetails],
  );

  const summaryItems = useMemo(
    () =>
      SUMMARY_ITEMS.map(({ label }) =>
        label === 'Number of positions'
          ? { label, value: positionCount }
          : { label, value: format({ value: unclaimedFees, type: DisplayType.Fiat }) },
      ),
    [positionCount, unclaimedFees],
  );

  return (
    <>
      {summaryItems.map((item) => {
        const testID =
          item.label === 'Number of positions'
            ? 'portfolio-positions-count'
            : item.label === 'Available fees'
            ? 'portfolio-total-value'
            : `portfolio-${item.label.toLowerCase().replace(' ', '-')}`;

        return (
          <VStack
            key={item.label}
            gap={1}
            flexBasis={SUMMARY_ITEMS_FLEX_BASIS}
            flexGrow={1}
            flexDirection={isMobile ? 'row' : 'column'}
          >
            <TextHeadline as="span" noWrap color="fgMuted">
              {item.label}
            </TextHeadline>
            <TextTitle1 as="span" testID={testID}>
              {item.value}
            </TextTitle1>
          </VStack>
        );
      })}
    </>
  );
}

const PortfolioSummaryContent = memo(function PortfolioSummaryContent() {
  const account = useAccount();

  const router = useRouter();

  const addLiquidity = useCallback(() => {
    void router.push('/add-liquidity');
  }, [router]);

  if (!account.address) {
    return null;
  }

  return (
    <VStack>
      <VStack gap={1} paddingX={3} width="100%">
        <HStack justifyContent="space-between" alignItems="center">
          <TextTitle3 as="h1">Earnings</TextTitle3>

          {/* TODO: Add back with better specs */}
          {account && false && (
            <PressableOpacity onClick={addLiquidity} noScaleOnPress>
              <HStack gap={1} alignItems="center">
                <Icon name="add" size="xs" active />
                <TextLabel1 as="span" color="fgPrimary">
                  Collect Fees
                </TextLabel1>
              </HStack>
            </PressableOpacity>
          )}
        </HStack>
      </VStack>
      <VStack padding={3} width="100%">
        <HStack
          justifyContent="flex-start"
          alignItems="center"
          gap={SUMMARY_ITEMS_GAP}
          flexWrap="wrap"
        >
          <Suspense fallback={<PortfolioSummaryItemsLoading />}>
            <PortfolioSummaryItems />
          </Suspense>
        </HStack>
      </VStack>
    </VStack>
  );
});

const PortfolioSummaryWithErrorBoundary = memo(function PortfolioSummaryWithErrorBoundary() {
  // hide the component if the account is not connected
  const account = useAccount();
  if (!account.address) {
    return null;
  }
  return (
    <VStack background="bg" borderRadius={300} paddingY={3} gap={3} testID="portfolio-summary">
      <ErrorBoundary message={ERROR_MESSAGE_PORTFOLIO_SUMMARY_FAILED}>
        <PortfolioSummaryContent />
      </ErrorBoundary>
    </VStack>
  );
});

export const PortfolioSummary = memo(function PortfolioSummary() {
  return <PortfolioSummaryWithErrorBoundary />;
});
